import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { createLead, listAdminUsersLight } from "../../lib/crmApi";
import { adminMe } from "../../lib/adminApi";

export default function CrmCreateLeadModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        company: "", contact_name: "", email: "", phone: "",
        address: "", postal_code: "", city: "",
        value_dkk: 0, notes: "", seller: "Cleany Support",
        source: "manuel", consent: false, owner_email: "",
    });
    const [saving, setSaving] = useState(false);
    const [team, setTeam] = useState([]);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        // Load team list + current user in parallel; default the owner to
        // the CURRENT logged-in user so a rep's new lead lands in their own
        // digest (matches the backend's None → creator default).
        Promise.all([
            listAdminUsersLight().catch(() => []),
            adminMe().catch(() => null),
        ]).then(([t, me]) => {
            setTeam(t);
            const defaultOwner = me?.email
                || (t.length > 0 ? t[0].email : "");
            if (defaultOwner) set("owner_email", defaultOwner);
        });
    }, []); // eslint-disable-line

    const submit = async (start_automation) => {
        if (!form.company || !form.contact_name || !form.email) {
            toast.error("Virksomhed, kontakt og email er påkrævet");
            return;
        }
        setSaving(true);
        try {
            await createLead({ ...form, value_dkk: Number(form.value_dkk) || 0, start_automation });
            toast.success(start_automation ? "Lead oprettet, flow startet" : "Lead oprettet");
            onCreated();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Fejl ved oprettelse";
            toast.error(msg);
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="crm-create-modal">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
                    <h2 className="font-display text-xl font-bold text-slate-900">Opret nyt lead</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="crm-create-close"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Virksomhed *" value={form.company} onChange={(v) => set("company", v)} testid="crm-company" />
                        <Field label="Kontaktperson *" value={form.contact_name} onChange={(v) => set("contact_name", v)} testid="crm-contact" />
                        <Field label="E-mail *" type="email" value={form.email} onChange={(v) => set("email", v)} testid="crm-email" />
                        <Field label="Telefon" value={form.phone} onChange={(v) => set("phone", v)} testid="crm-phone" />
                        <Field label="Adresse" value={form.address} onChange={(v) => set("address", v)} />
                        <Field label="Postnr" value={form.postal_code} onChange={(v) => set("postal_code", v)} />
                        <Field label="By" value={form.city} onChange={(v) => set("city", v)} />
                        <Field label="Leadværdi (DKK)" type="number" value={form.value_dkk} onChange={(v) => set("value_dkk", v)} testid="crm-value" />
                        <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Leadkilde</label>
                            <select value={form.source} onChange={(e) => set("source", e.target.value)}
                                className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm">
                                {["manuel", "hjemmeside", "meta", "google", "messe", "anbefaling"].map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                        <Field label="Ansvarlig sælger" value={form.seller} onChange={(v) => set("seller", v)} />
                        <div>
                            <label className="block text-xs font-medium text-slate-700 mb-1">Lead-ejer (får dagens plan)</label>
                            <select value={form.owner_email} onChange={(e) => set("owner_email", e.target.value)}
                                data-testid="crm-owner"
                                className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm">
                                <option value="">Ingen (vises for alle)</option>
                                {team.map((u) => (
                                    <option key={u.email} value={u.email}>
                                        {u.name} · {u.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Noter</label>
                        <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                            className="w-full rounded-lg border border-slate-300 p-3 text-sm" data-testid="crm-notes" />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="crm-consent" />
                        Der er lovligt behandlingsgrundlag (samtykke / kontrakt / legitim interesse)
                    </label>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap gap-3 justify-end sticky bottom-0 bg-white">
                    <button type="button" onClick={onClose} className="rounded-lg px-4 h-10 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuller</button>
                    <button type="button" disabled={saving} onClick={() => submit(false)} data-testid="crm-save-no-flow"
                        className="rounded-lg px-4 h-10 text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50">
                        Gem uden automation
                    </button>
                    <button type="button" disabled={saving} onClick={() => submit(true)} data-testid="crm-save-with-flow"
                        className="rounded-lg px-4 h-10 text-sm font-semibold bg-sky-600 hover:bg-sky-700 text-white inline-flex items-center gap-2">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Gem og start e-mailflow
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="w-full rounded-lg border border-slate-300 h-10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
        </div>
    );
}
